import type { Color, GradientStop, Paint } from './types.ts';

// ---------------------------------------------------------------------------
// Fractional indexing: ordered keys you can always insert between, so concurrent
// reorders from different clients never need to rewrite sibling indices.
// ---------------------------------------------------------------------------

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;

function digit(s: string, i: number): number {
  return i < s.length ? DIGITS.indexOf(s[i]) : 0;
}

/** Returns a key strictly between a and b (either may be null for open ends). */
export function indexBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) {
    // Corrupt order; fall back to appending after a.
    b = null;
  }
  let result = '';
  for (let i = 0; ; i++) {
    const da = a === null ? 0 : digit(a, i);
    const db = b === null ? BASE : i < b.length ? digit(b, i) : BASE;
    if (db - da > 1) {
      result += DIGITS[Math.floor((da + db) / 2)];
      return result;
    }
    result += DIGITS[da];
    if (db - da === 1) {
      // Same prefix range exhausted at this digit for b; continue with b open.
      b = null;
    }
  }
}

/** n evenly spread keys between a and b. */
export function indicesBetween(a: string | null, b: string | null, n: number): string[] {
  const out: string[] = [];
  let lo = a;
  for (let i = 0; i < n; i++) {
    const k = indexBetween(lo, b);
    out.push(k);
    lo = k;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

let counter = 0;
const session = Math.random().toString(36).slice(2, 6);
export function newId(): string {
  counter++;
  return `${session}${counter.toString(36)}`;
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

export function rgba(r: number, g: number, b: number, a = 1): Color {
  return { r, g, b, a };
}

const NAMED: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  red: '#f24822',
  orange: '#ffa629',
  yellow: '#ffcd29',
  green: '#14ae5c',
  teal: '#0fa5a5',
  blue: '#0d99ff',
  violet: '#9747ff',
  purple: '#9747ff',
  pink: '#ff24bd',
  gray: '#b3b3b3',
  grey: '#b3b3b3',
  transparent: '#00000000',
};

/** Parse "#rgb", "#rrggbb", "#rrggbbaa", "rgb(...)", "rgba(...)" or a named color. */
export function parseColor(input: string | Color): Color {
  if (typeof input !== 'string') return input;
  let s = input.trim().toLowerCase();
  if (NAMED[s]) s = NAMED[s];
  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (h.length === 3 || h.length === 4) h = [...h].map((c) => c + c).join('');
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16) / 255;
    return { r: n(0), g: n(2), b: n(4), a: h.length >= 8 ? n(6) : 1 };
  }
  const m = s.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const p = m[1].split(/[ ,/]+/).filter(Boolean).map(Number);
    return { r: p[0] / 255, g: p[1] / 255, b: p[2] / 255, a: p[3] ?? 1 };
  }
  throw new Error(`Unrecognized color: ${input}`);
}

export function colorToHex(c: Color, withAlpha = c.a < 1): string {
  const h = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${h(c.r)}${h(c.g)}${h(c.b)}${withAlpha ? h(c.a) : ''}`;
}

export function colorToCss(c: Color, extraAlpha = 1): string {
  const a = c.a * extraAlpha;
  return `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${+a.toFixed(4)})`;
}

export function solid(color: string | Color, opacity?: number): Paint {
  const p: Paint = { type: 'SOLID', color: parseColor(color) };
  if (opacity !== undefined) p.opacity = opacity;
  return p;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function deepClone<T>(v: T): T {
  return v === undefined ? v : structuredClone(v);
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  }
  return true;
}

/** CSS filter for image adjustments (each -1…1); '' when there is nothing to apply. */
export function imageFilterCss(f?: { exposure?: number; contrast?: number; saturation?: number; temperature?: number }): string {
  if (!f) return '';
  const parts: string[] = [];
  if (f.exposure) parts.push(`brightness(${(1 + f.exposure).toFixed(3)})`);
  if (f.contrast) parts.push(`contrast(${(1 + f.contrast).toFixed(3)})`);
  if (f.saturation) parts.push(`saturate(${(1 + f.saturation).toFixed(3)})`);
  if (f.temperature) parts.push(f.temperature > 0 ? `sepia(${(f.temperature * 0.5).toFixed(3)})` : `hue-rotate(${(f.temperature * 30).toFixed(1)}deg)`);
  return parts.join(' ');
}

/** Any gradient paint (linear, radial, angular, diamond). */
/** The CSS / Canvas name for a blend mode (null: normal compositing). Linear burn has no CSS twin; multiply is the closest. */
export function blendCss(mode: string | undefined): GlobalCompositeOperation | null {
  if (!mode || mode === 'NORMAL' || mode === 'PASS_THROUGH') return null;
  if (mode === 'LINEAR_DODGE') return 'lighter';
  if (mode === 'LINEAR_BURN') return 'multiply';
  return mode.toLowerCase().replace(/_/g, '-') as GlobalCompositeOperation;
}

export function isGradient(p: { type: string }): p is Extract<Paint, { stops: GradientStop[] }> {
  return p.type === 'GRADIENT_LINEAR' || p.type === 'GRADIENT_RADIAL' || p.type === 'GRADIENT_ANGULAR' || p.type === 'GRADIENT_DIAMOND';
}
